import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  FC,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import clsx from 'clsx';
import {
  secondsToShortString,
  secondsToHoursTwoDecimals,
} from 'packages/lib/utils/time';
import CopyToken from 'packages/lib/common-ui/copy-token';
import { useMapState } from 'packages/lib/utils/hooks/use-map-state';
import { Typography, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { formatDateTime } from 'packages/lib/utils/date';
import { CopyOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { css } from '@emotion/css';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';

// Jul-08-2026 Used Cursor (Auto) to modify keyValueTable to have compact them, tooltip, muted option, DateTime formatting, and support external links with custom text labels

const KEY_VALUE_TABLE_TOOLTIP_WIDTH = 500;

const keyValueTableTooltipOverlayClass = css`
  .ant-tooltip-inner {
    min-width: ${KEY_VALUE_TABLE_TOOLTIP_WIDTH}px;
    max-width: ${KEY_VALUE_TABLE_TOOLTIP_WIDTH}px;
  }
`;

const DEFAULT_ROW_HEIGHT = 10;
const COMPACT_ROW_HEIGHT = 0;

const useLightTableStyles = makeStyles<{
  rowHeight: number;
  compact: boolean;
}>()((_theme, { rowHeight, compact }) => ({
  table: {
    borderCollapse: 'collapse',
    '& th, tr': {
      textAlign: 'left',
    },
    '& td': {
      borderStyle: 'solid',
      borderColor: 'lightgray',
      borderWidth: '1px 0px',
      borderLeft: '0px',
      borderRight: '0px',
    },
    '& tr:first-of-type td': {
      borderTop: '0px',
    },
    '& tr:last-child td': {
      borderBottom: '0px',
    },
  },
  key: {
    fontWeight: 600,
    padding: compact ? '4px 10px' : '8px 14px',
    fontSize: compact ? '0.8125rem' : undefined,
    lineHeight: compact ? 1.25 : undefined,
  },
  value: {
    paddingRight: compact ? '8px' : '10px',
    width: '100%',
    fontSize: compact ? '0.8125rem' : undefined,
    lineHeight: compact ? 1.25 : undefined,
  },
  wordBreak: {
    wordBreak: 'break-word',
    paddingTop: rowHeight,
    paddingBottom: rowHeight,
  },
  ellipsis: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    paddingTop: rowHeight,
    paddingBottom: rowHeight,
  },
  openInNewIcon: {
    position: 'relative',
    top: '3px',
    height: '20px',
    paddingTop: '4px',
  },
}));

export type KeyValueObj = Record<
  string | number,
  string | number | boolean | null
>;

export type KeyValueTableDataType =
  | string
  | string[]
  | KeyValueObj
  | Record<string, unknown>
  | number
  | null
  | undefined;

export interface KeyValueTableRow {
  key: string;
  value: KeyValueTableDataType;
  type?:
    | 'seconds'
    | 'hoursTwoDecimals'
    | 'link'
    | 'formattedTimesstamp'
    | 'datetime'; // i.e. "2026-06-05T20:38:29.274Z"
  linkLabel?: string; // Use when you want the link to show more user-friendly text instead of the actual link
  noWrap?: boolean;
  allowCopy?: boolean;
  maxStrLen?: number; // number of characters to truncate to
  tooltip?: ReactNode;
  muted?: boolean;
}
interface KeyValueTableProps {
  tableNameKey: string;
  data: KeyValueTableRow[];
  keyColWidth: number;
  maxValueWidth: number;
  noLines?: boolean;
  compact?: boolean;
  rowHeight?: number;
}

const isExternalHref = (href: string): boolean => /^https?:\/\//.test(href);

const renderKeyValueLinkCell = (
  row: KeyValueTableRow,
  mutedStyle: CSSProperties | undefined,
  openInNewIconClassName: string
): ReactNode => {
  const href = typeof row.value === 'string' ? row.value : null;
  if (!href) {
    return '--';
  }

  if (row.linkLabel) {
    if (isExternalHref(href)) {
      return (
        <a href={href} target="_blank" rel="noreferrer" style={mutedStyle}>
          {row.linkLabel}
        </a>
      );
    }

    return (
      <Link to={href} style={mutedStyle}>
        {row.linkLabel}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" style={mutedStyle}>
      Link
      <OpenInNewIcon className={openInNewIconClassName} />
    </a>
  );
};

/** 
  For table data, each row has an option 'type' that can be any of: 'seconds' | 'hoursTwoDecimals' | 'link' | 'formattedTimesstamp' | 'datetime'
  Each row can also have these optional booleans: noWrap, allowCopy

  @param compact reduces row padding and font size for denser tables
  @param rowHeight specifies the whitespace padding for each row. Use 1 for more compact tables.
*/
export const KeyValueTable: FC<React.PropsWithChildren<KeyValueTableProps>> = ({
  data,
  keyColWidth,
  maxValueWidth,
  tableNameKey,
  compact = false,
  rowHeight,
}) => {
  const resolvedRowHeight =
    rowHeight ?? (compact ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT);
  const { classes: tableStyles } = useLightTableStyles({
    rowHeight: resolvedRowHeight,
    compact,
  });

  const [rowHasEllipsis, setRowHasEllipsis] = useMapState<string, boolean>(
    new Map()
  );
  const [expandedRows, setExpandedRowsKey] = useMapState<string, boolean>(
    new Map()
  );

  return (
    <table data-testid="dag-stats-table" className={tableStyles.table}>
      <tbody>
        {data.map((row) => (
          <tr key={tableNameKey + '-' + row.key}>
            <KeyValueCells
              row={row}
              expandedRows={expandedRows}
              tableStyles={tableStyles}
              setExpandedRowsKey={setExpandedRowsKey}
              rowHasEllipsis={rowHasEllipsis}
              setRowHasEllipsis={setRowHasEllipsis}
              keyColWidth={keyColWidth}
              maxValueWidth={maxValueWidth}
            />
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface MyNewFCProps {
  row: KeyValueTableRow;
  expandedRows: Map<string, boolean>;
  setExpandedRowsKey: (key: string, value: boolean) => void;
  rowHasEllipsis: Map<string, boolean>;
  setRowHasEllipsis: (key: string, value: boolean) => void;
  tableStyles: Record<string, string>;
  keyColWidth: number;
  maxValueWidth: number;
}

export const KeyValueCells: FC<React.PropsWithChildren<MyNewFCProps>> = ({
  row,
  expandedRows,
  setExpandedRowsKey,
  rowHasEllipsis,
  setRowHasEllipsis,
  tableStyles,
  keyColWidth,
  maxValueWidth,
  children,
}) => {
  const tdRef = useRef<HTMLTableCellElement>(null);

  const theme = useRecoilValue(uiThemeAtom);
  const linkBlueColor = theme === 'dark' ? '#6babfeff' : 'default';
  const tooltipIconColor =
    theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)';
  const mutedTextColor =
    theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)';
  const mutedStyle = row.muted ? { color: mutedTextColor } : undefined;
  const copyIcon = <CopyOutlined style={{ color: linkBlueColor }} />;

  // This is how we can tell if a CSS Ellipsis is being shown in the Value cell, due to a line being too long, and being clipped with an ellipsis.
  // This lets us show an expand triangle for that item when appropriate.
  useEffect(() => {
    if (tdRef.current) {
      const hasEllipsis =
        tdRef.current?.scrollWidth > tdRef.current?.clientWidth;
      setRowHasEllipsis(row.key, hasEllipsis);
    }
  }, [row.key, tdRef, setRowHasEllipsis]);

  return (
    <>
      <td
        className={tableStyles.key}
        style={{ minWidth: keyColWidth, ...mutedStyle }}
      >
        <span>{row.key}</span>
        {row.tooltip && (
          <Tooltip
            title={row.tooltip}
            classNames={{ root: keyValueTableTooltipOverlayClass }}
            styles={{
              body: {
                minWidth: KEY_VALUE_TABLE_TOOLTIP_WIDTH,
                maxWidth: KEY_VALUE_TABLE_TOOLTIP_WIDTH,
              },
            }}
          >
            <span
              style={{
                marginLeft: '4px',
                display: 'inline-flex',
                verticalAlign: 'middle',
                cursor: 'help',
              }}
            >
              <QuestionCircleOutlined
                style={{ fontSize: '14px', color: tooltipIconColor }}
              />
            </span>
          </Tooltip>
        )}
        {typeof row.value === 'object' &&
          row.value !== null &&
          rowHasEllipsis.get(row.key) && (
            <IconButton
              size="small"
              onClick={(): void =>
                setExpandedRowsKey(row.key, !expandedRows.get(row.key))
              }
            >
              {expandedRows.get(row.key) ? (
                <ArrowDownIcon style={{ fontSize: '1.2rem' }} />
              ) : (
                <ArrowRightIcon style={{ fontSize: '1.2rem' }} />
              )}
            </IconButton>
          )}
      </td>
      <td
        ref={tdRef}
        style={{
          maxWidth:
            typeof row.value === 'object' && !expandedRows.get(row.key)
              ? maxValueWidth
              : undefined,
          ...mutedStyle,
        }}
        className={clsx(
          tableStyles.value,
          row.noWrap ||
            (typeof row.value === 'object' && !expandedRows.get(row.key))
            ? tableStyles.ellipsis
            : tableStyles.wordBreak
        )}
      >
        {row.type === 'seconds' &&
          secondsToShortString(row.value as string, '--')}
        {row.type === 'hoursTwoDecimals' &&
          secondsToHoursTwoDecimals(row.value as string, '--')}
        {row.type === 'link' &&
          renderKeyValueLinkCell(row, mutedStyle, tableStyles.openInNewIcon)}
        {typeof row.value === 'object' && row.value !== null && (
          <>
            <CopyToken
              copyText={JSON.stringify(row.value)}
              label={row.key}
              iconSize="16px"
            />

            {JSON.stringify(row.value)}
          </>
        )}
        {row.type === 'formattedTimesstamp' && (
          <Typography.Text
            type={row.muted ? 'secondary' : undefined}
            copyable={{ icon: copyIcon, text: row.value?.toString() }}
          >
            {row.value
              ? formatDateTime(new Date(Number(row.value) / 1000))
              : '-'}
          </Typography.Text>
        )}
        {row.type === 'datetime' && (
          <Typography.Text
            type={row.muted ? 'secondary' : undefined}
            copyable={{ icon: copyIcon, text: row.value?.toString() }}
          >
            {typeof row.value === 'string' && row.value
              ? formatDateTime(
                  new Date(
                    row.value.endsWith('Z') ? row.value : `${row.value}Z`
                  )
                )
              : '--'}
          </Typography.Text>
        )}
        {row.type !== 'seconds' &&
          row.type !== 'hoursTwoDecimals' &&
          row.type !== 'link' &&
          row.type !== 'formattedTimesstamp' &&
          row.type !== 'datetime' &&
          (typeof row.value !== 'object' || row.value === null) && (
            <>
              {row.value ? (
                <span>
                  <Typography.Text
                    type={row.muted ? 'secondary' : undefined}
                    copyable={
                      row.allowCopy || row.noWrap
                        ? { text: row.value?.toString(), icon: copyIcon }
                        : false
                    }
                  >
                    {row.maxStrLen && typeof row.value === 'string'
                      ? row.value.substring(0, row.maxStrLen) + '...'
                      : row.value}
                  </Typography.Text>
                </span>
              ) : (
                '--'
              )}
            </>
          )}
      </td>
    </>
  );
};
